import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL, convertImageUrl } from './config';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'host' | 'admin';
}

interface TransferItem {
  id: number;
  user_id: string;
  user_name: string;
  title: string;
  deal_type: string;
  address: string;
  price: number;
  occupancy_rate: number | null;
  fixed_cost: number | null;
  monthly_income: number | null;
  description: string;
  images: string[];
  status: string;
  created_at: string;
  contact?: string;
}

interface Props {
  token: string;
  user: AuthUser | null;
}

function formatNumber(n: number | null | undefined): string {
  if (n == null) return '-';
  return n.toLocaleString('ko-KR');
}

function formatPrice(n: number): string {
  if (n >= 100000000) return `${(n / 100000000).toFixed(n % 100000000 === 0 ? 0 : 1)}억`;
  if (n >= 10000) return `${formatNumber(Math.round(n / 10000))}만`;
  return formatNumber(n);
}

function fmtDate(iso: string) {
  if (!iso) return '';
  const utc = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(utc);
  return d.toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' });
}

export default function TransferBoard({ token, user }: Props) {
  const [transfers, setTransfers] = useState<TransferItem[]>([]);
  const [filter, setFilter] = useState<string>('전체');
  const [showWrite, setShowWrite] = useState(false);
  const [detailItem, setDetailItem] = useState<TransferItem | null>(null);
  const [detailSlide, setDetailSlide] = useState(0);
  const [contactInfo, setContactInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [writeForm, setWriteForm] = useState({
    title: '', deal_type: '매매', address: '', price: '',
    occupancy_rate: '', fixed_cost: '', monthly_income: '',
    description: '', contact: '', images: [] as string[],
  });
  const fileRef = useRef<HTMLInputElement>(null);

  const headers: Record<string, string> = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchTransfers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/transfers`, { headers });
      if (res.ok) setTransfers(await res.json());
    } catch {}
  }, [token]);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  const filtered = filter === '전체' ? transfers : transfers.filter((t) => t.deal_type === filter);

  const uploadImage = async (file: File): Promise<string | null> => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_BASE_URL}/api/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        return data.file_url || data.url;
      }
    } catch {}
    return null;
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArr = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (fileArr.length === 0) return;
    const remaining = 20 - writeForm.images.length;
    const toUpload = fileArr.slice(0, remaining);
    if (toUpload.length === 0) { alert('사진은 최대 20장까지 등록 가능합니다'); return; }
    setUploading(true);
    const urls: string[] = [];
    for (const f of toUpload) {
      const url = await uploadImage(f);
      if (url) urls.push(url);
    }
    setWriteForm((p) => ({ ...p, images: [...p.images, ...urls] }));
    setUploading(false);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) { e.preventDefault(); handleFiles(files); }
  };

  const handleWrite = async () => {
    if (!writeForm.title.trim()) { alert('제목을 입력하세요'); return; }
    if (!writeForm.address.trim()) { alert('주소를 입력하세요'); return; }
    if (!writeForm.price) { alert('매도가격을 입력하세요'); return; }
    if (!writeForm.contact.trim()) { alert('연락처를 입력하세요'); return; }
    if (writeForm.images.length < 5) { alert('사진은 5장 이상 등록해주세요'); return; }
    setSubmitting(true);
    try {
      const body = {
        title: writeForm.title,
        deal_type: writeForm.deal_type,
        address: writeForm.address,
        price: parseInt(writeForm.price) || 0,
        occupancy_rate: writeForm.occupancy_rate ? parseInt(writeForm.occupancy_rate) : null,
        fixed_cost: writeForm.fixed_cost ? parseInt(writeForm.fixed_cost) : null,
        monthly_income: writeForm.monthly_income ? parseInt(writeForm.monthly_income) : null,
        description: writeForm.description,
        contact: writeForm.contact,
        images: writeForm.images,
      };
      const res = await fetch(`${API_BASE_URL}/api/transfers`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      if (res.ok) {
        setShowWrite(false);
        setWriteForm({ title: '', deal_type: '매매', address: '', price: '', occupancy_rate: '', fixed_cost: '', monthly_income: '', description: '', contact: '', images: [] });
        fetchTransfers();
      } else {
        const err = await res.json();
        alert(err.detail || '등록 실패');
      }
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 매물을 삭제하시겠습니까?')) return;
    await fetch(`${API_BASE_URL}/api/transfers/${id}`, { method: 'DELETE', headers });
    setDetailItem(null);
    fetchTransfers();
  };

  const handleContact = async (id: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/transfers/${id}/contact`, {
        method: 'POST', headers,
      });
      if (res.ok) {
        const data = await res.json();
        setContactInfo(data.contact);
      }
    } catch {}
  };

  const openDetail = (item: TransferItem) => {
    setDetailItem(item);
    setDetailSlide(0);
    setContactInfo(null);
  };

  const dealBadge = (type: string) => {
    const isSale = type === '매매';
    return (
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
        background: isSale ? '#dbeafe' : '#fef3c7',
        color: isSale ? '#1d4ed8' : '#b45309',
      }}>{type}</span>
    );
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px', border: '1px solid #e2e5e9',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }} onPaste={handlePaste}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>양도 / 양수</h2>
        <button
          onClick={() => setShowWrite(true)}
          style={{ padding: '7px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
        >+ 매물 등록</button>
      </div>

      {/* 필터 */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {['전체', '매매', '권리양수도'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '6px 14px', fontSize: 13, borderRadius: 20, cursor: 'pointer',
              border: filter === f ? '1px solid #2563eb' : '1px solid #e2e5e9',
              background: filter === f ? '#eff6ff' : '#fff',
              color: filter === f ? '#2563eb' : '#666',
              fontWeight: filter === f ? 600 : 400,
            }}
          >{f}</button>
        ))}
      </div>

      {/* 리스트 */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '60px 0', fontSize: 14 }}>등록된 매물이 없습니다.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map((t) => (
            <div
              key={t.id}
              onClick={() => openDetail(t)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
                background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#2563eb'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#e8ecf0'; }}
            >
              {/* 썸네일 */}
              <div style={{ width: 80, height: 60, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: '#f1f5f9' }}>
                {t.images.length > 0 && (
                  <img src={convertImageUrl(t.images[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>
              {/* 정보 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  {dealBadge(t.deal_type)}
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.address}</div>
              </div>
              {/* 가격/수익 */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1d4ed8' }}>{formatPrice(t.price)}원</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {t.occupancy_rate != null && <span>가동률 {t.occupancy_rate}%</span>}
                  {t.monthly_income != null && <span style={{ marginLeft: 8 }}>월수익 {formatPrice(t.monthly_income)}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 등록 모달 */}
      {showWrite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowWrite(false); }}
        >
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: 'min(92vw, 560px)', maxHeight: '85vh', overflow: 'auto', boxSizing: 'border-box' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 20px' }}>매물 등록</h3>

            {/* 거래유형 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 6 }}>거래유형 *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['매매', '권리양수도'].map((dt) => (
                  <button
                    key={dt}
                    onClick={() => setWriteForm((p) => ({ ...p, deal_type: dt }))}
                    style={{
                      padding: '8px 20px', fontSize: 13, borderRadius: 8, cursor: 'pointer',
                      border: writeForm.deal_type === dt ? '2px solid #2563eb' : '1px solid #e2e5e9',
                      background: writeForm.deal_type === dt ? '#eff6ff' : '#fff',
                      color: writeForm.deal_type === dt ? '#2563eb' : '#666',
                      fontWeight: writeForm.deal_type === dt ? 600 : 400,
                    }}
                  >{dt}</button>
                ))}
              </div>
            </div>

            {/* 제목 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>제목 *</label>
              <input style={inputStyle} value={writeForm.title} onChange={(e) => setWriteForm((p) => ({ ...p, title: e.target.value }))} placeholder="예: 강남역 오피스텔 양도" />
            </div>

            {/* 주소 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>주소 *</label>
              <input style={inputStyle} value={writeForm.address} onChange={(e) => setWriteForm((p) => ({ ...p, address: e.target.value }))} placeholder="서울시 강남구 역삼동 123" />
            </div>

            {/* 가격 + 가동률 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>매도가격 (원) *</label>
                <input style={inputStyle} type="number" value={writeForm.price} onChange={(e) => setWriteForm((p) => ({ ...p, price: e.target.value }))} placeholder="50000000" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>가동률 (%)</label>
                <input style={inputStyle} type="number" value={writeForm.occupancy_rate} onChange={(e) => setWriteForm((p) => ({ ...p, occupancy_rate: e.target.value }))} placeholder="85" />
              </div>
            </div>

            {/* 고정비 + 월수익 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>고정비 (원/월)</label>
                <input style={inputStyle} type="number" value={writeForm.fixed_cost} onChange={(e) => setWriteForm((p) => ({ ...p, fixed_cost: e.target.value }))} placeholder="500000" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>월 수익 (원)</label>
                <input style={inputStyle} type="number" value={writeForm.monthly_income} onChange={(e) => setWriteForm((p) => ({ ...p, monthly_income: e.target.value }))} placeholder="1500000" />
              </div>
            </div>

            {/* 설명 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>상세 설명</label>
              <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={writeForm.description} onChange={(e) => setWriteForm((p) => ({ ...p, description: e.target.value }))} placeholder="매물 상세 설명..." />
            </div>

            {/* 연락처 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>연락처 *</label>
              <input style={inputStyle} value={writeForm.contact} onChange={(e) => setWriteForm((p) => ({ ...p, contact: e.target.value }))} placeholder="010-1234-5678" />
            </div>

            {/* 이미지 업로드 */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 6 }}>사진 ({writeForm.images.length}/20, 최소 5장) *</label>
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }} />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                style={{ padding: '8px 16px', border: '1px dashed #cbd5e1', borderRadius: 8, background: '#f8fafc', color: '#64748b', fontSize: 13, cursor: 'pointer', marginBottom: 8 }}
              >
                {uploading ? '업로드 중...' : '📷 사진 선택 (또는 Ctrl+V 붙여넣기)'}
              </button>
              {writeForm.images.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {writeForm.images.map((url, i) => (
                    <div key={i} style={{ position: 'relative', width: 72, height: 54 }}>
                      <img src={convertImageUrl(url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e5e9' }} />
                      <button
                        onClick={() => setWriteForm((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }))}
                        style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#ef4444', color: '#fff', border: 'none', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                      >x</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 버튼 */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowWrite(false)} style={{ padding: '8px 16px', border: '1px solid #e2e5e9', borderRadius: 8, background: '#f5f5f5', color: '#333', fontSize: 13, cursor: 'pointer' }}>취소</button>
              <button onClick={handleWrite} disabled={submitting} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                {submitting ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 상세 팝업 */}
      {detailItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={(e) => { if (e.target === e.currentTarget) { setDetailItem(null); setContactInfo(null); } }}
        >
          <div style={{ background: '#fff', borderRadius: 14, width: 'min(92vw, 640px)', maxHeight: '90vh', overflow: 'auto', boxSizing: 'border-box' }}>
            {/* 사진 슬라이더 */}
            {detailItem.images.length > 0 && (
              <div style={{ position: 'relative', background: '#000', borderRadius: '14px 14px 0 0', overflow: 'hidden' }}>
                <img
                  src={convertImageUrl(detailItem.images[detailSlide])}
                  alt=""
                  style={{ width: '100%', height: 360, objectFit: 'contain', display: 'block' }}
                />
                {/* 좌우 버튼 */}
                {detailItem.images.length > 1 && (
                  <>
                    <button
                      onClick={() => setDetailSlide((s) => (s - 1 + detailItem.images.length) % detailItem.images.length)}
                      style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.8)', border: 'none', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >‹</button>
                    <button
                      onClick={() => setDetailSlide((s) => (s + 1) % detailItem.images.length)}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.8)', border: 'none', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >›</button>
                  </>
                )}
                {/* 인디케이터 */}
                <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 12, padding: '3px 10px', borderRadius: 12 }}>
                  {detailSlide + 1} / {detailItem.images.length}
                </div>
              </div>
            )}

            {/* 매물 정보 */}
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    {dealBadge(detailItem.deal_type)}
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(detailItem.created_at)}</span>
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>{detailItem.title}</h3>
                </div>
                {(user?.role === 'admin' || user?.id === detailItem.user_id) && (
                  <button
                    onClick={() => handleDelete(detailItem.id)}
                    style={{ fontSize: 12, color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
                  >삭제</button>
                )}
              </div>

              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{detailItem.address}</div>

              {/* 수치 정보 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16, background: '#f8fafc', borderRadius: 10, padding: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>매도가격</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1d4ed8' }}>{formatNumber(detailItem.price)}원</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>가동률</div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{detailItem.occupancy_rate != null ? `${detailItem.occupancy_rate}%` : '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>고정비 (월)</div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{detailItem.fixed_cost != null ? `${formatNumber(detailItem.fixed_cost)}원` : '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>월 수익</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#059669' }}>{detailItem.monthly_income != null ? `${formatNumber(detailItem.monthly_income)}원` : '-'}</div>
                </div>
              </div>

              {/* 설명 */}
              {detailItem.description && (
                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 20, padding: '12px 16px', background: '#fafafa', borderRadius: 8 }}>
                  {detailItem.description}
                </div>
              )}

              {/* 문의하기 */}
              <div style={{ borderTop: '1px solid #e8ecf0', paddingTop: 16 }}>
                {contactInfo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#eff6ff', borderRadius: 8 }}>
                    <span style={{ fontSize: 13, color: '#475569' }}>연락처:</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#1d4ed8' }}>{contactInfo}</span>
                  </div>
                ) : (
                  <button
                    onClick={() => handleContact(detailItem.id)}
                    style={{ width: '100%', padding: '12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
                  >문의하기</button>
                )}
              </div>
            </div>

            {/* 닫기 */}
            <button
              onClick={() => { setDetailItem(null); setContactInfo(null); }}
              style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >×</button>
          </div>
        </div>
      )}
    </div>
  );
}
